import React, { useEffect, useState, Children, createElement } from 'react';
import { Header } from '../components/Header';
import { VideoPlayer } from '../components/VideoPlayer';
import { CustomVideoPlayer } from '../components/CustomVideoPlayer';
import { HeroHighlight, Highlight } from '../components/ui/HeroHighlight';
import { BookCallButton } from '../components/ui/BookCallButton';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const vttContent = `WEBVTT

00:00:00.000 --> 00:00:05.000
All right, you are on a roll. This is the final step to the onboarding process here.

00:00:05.000 --> 00:00:10.000
And that is you need to grant us access to your Indeed account as an admin.

00:00:10.000 --> 00:00:15.000
Now I'm going to show you a step-by-step process of how to do this, and it's going to be a very, very simple process.

00:00:15.000 --> 00:00:20.000
But we need this in order to run the campaigns for you on your behalf.

00:00:20.000 --> 00:00:25.000
Now this is important because if we do not get this access, we will not be able to test the ads quickly.

00:00:25.000 --> 00:00:30.000
And the sooner we can get that done, the faster we can get this build launched.

00:00:30.000 --> 00:00:35.000
So, let me go ahead and show you a step-by-step process on how to do this.

00:00:35.000 --> 00:00:40.000
So, if you go ahead and log into your Indeed account, you're going to click your initials up here on the top right.

00:00:40.000 --> 00:00:45.000
And then the next step is to go ahead and click into users.

00:00:45.000 --> 00:00:50.000
From the users, you're going to then click add users, which will be this button right here.

00:00:50.000 --> 00:00:55.000
Type in indeed@thehireadvantage.co. This is the email that grants us access to our entire team.

00:00:55.000 --> 00:01:00.000
Rayna, which is your onboarding specialist, is the one that's going to monitor this,

00:01:00.000 --> 00:01:05.000
and she will be able to give all of the other folks access to the Indeed account.

00:01:05.000 --> 00:01:10.000
Now, one thing that is extremely important to make sure you check off on is that you select the top button here,

00:01:10.000 --> 00:01:15.000
which is the star admin. This will allow us to be able to have full access to be able to run those ads on your behalf.

00:01:15.000 --> 00:01:20.000
Please let us know if you have any questions or if there's anything that is confusing about this.

00:01:20.000 --> 00:01:25.000
We're happy to work with you guys and excited for the rest of this.`;
export function IndeedPage() {
  const [vttUrl, setVttUrl] = useState('');
  const [activeTab, setActiveTab] = useState(1);
  useEffect(() => {
    window.scrollTo(0, 0);
    const blob = new Blob([vttContent], {
      type: 'text/vtt'
    });
    const url = URL.createObjectURL(blob);
    setVttUrl(url);
    return () => URL.revokeObjectURL(url);
  }, []);
  const navigate = useNavigate();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100,
        duration: 0.4
      }
    }
  };
  return <div className="w-full h-screen overflow-hidden" data-id="element-281">
      <HeroHighlight data-id="element-282">
        <div className="h-screen flex flex-col" data-id="element-283">
          <Header data-id="element-284" />
          <motion.main className="flex-1 flex flex-col justify-center max-w-5xl mx-auto px-4 w-full" initial="hidden" animate="visible" variants={containerVariants} data-id="element-285">
            <motion.div variants={itemVariants} data-id="element-286">
              <div className="flex justify-center mb-2 sm:mb-3 items-center" data-id="element-287">
                <div className="flex items-center" data-id="element-288">
                  <motion.button onClick={() => navigate('/dns')} className="mr-2 h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} data-id="element-289">
                    <ChevronLeftIcon className="w-4 h-4" data-id="element-290" />
                  </motion.button>
                  <div className="inline-flex items-center px-3 sm:px-5 py-1 sm:py-1.5 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm" data-id="element-291">
                    <span className="text-xs sm:text-sm font-medium text-white" data-id="element-292">
                      STEP
                    </span>
                    <div className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 mx-1 sm:mx-1.5 rounded-full bg-white" data-id="element-293">
                      <span className="text-xs sm:text-sm font-bold text-gray-900" data-id="element-294">
                        4
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-white" data-id="element-295">
                      OF 4
                    </span>
                  </div>
                </div>
              </div>
              <h1 className="text-center text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 max-w-3xl mx-auto leading-[1.3] text-white" data-id="element-296">
                Grant us Admin Access to Indeed and Setup Payment Method
              </h1>

              <div className="relative max-w-3xl mx-auto w-full" data-id="element-297">
                <div className="flex gap-2 mb-3 justify-center" data-id="element-298">
                  <button onClick={() => setActiveTab(1)} className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${activeTab === 1 ? 'bg-[#7fe0c3] text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-299">
                    Part 1
                  </button>
                  <button onClick={() => setActiveTab(2)} className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${activeTab === 2 ? 'bg-[#7fe0c3] text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-300">
                    Part 2
                  </button>
                </div>

                <div className={activeTab === 1 ? 'block' : 'hidden'} data-id="element-301">
                  <VideoPlayer mediaId="xt0tagpnnt" onEnded={() => setActiveTab(2)} autoPlay={true} data-id="element-302" />
                </div>
                <div className={activeTab === 2 ? 'block' : 'hidden'} data-id="element-303">
                  <CustomVideoPlayer src="https://cdn.magicpatterns.com/uploads/7RpzGfpfwbTvr3mG1ehPTr/How%20to%20Add%20Admins%20to%20Your%20Indeed%20Account%20for%20Onboarding%20%F0%9F%9A%80%20(1).mp4" autoPlay={activeTab === 2} vttUrl={vttUrl} data-id="element-304" />
                </div>
                <motion.button onClick={() => navigate('/completion')} className="hidden md:flex flex-col items-center gap-1 px-3 py-4 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors cursor-pointer absolute -right-16" style={{
                top: '50%',
                y: '-50%'
              }} whileHover={{
                scale: 1.05,
                x: 4
              }} whileTap={{
                scale: 0.95
              }} data-id="element-305">
                  <ChevronRightIcon className="w-6 h-6" data-id="element-306" />
                  <span className="text-xs font-semibold whitespace-nowrap" data-id="element-307">
                    Next
                  </span>
                </motion.button>
              </div>
              <motion.button onClick={() => navigate('/completion')} className="md:hidden flex items-center justify-center gap-2 mx-auto mt-3 py-2.5 px-6 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors cursor-pointer" whileTap={{
              scale: 0.95
            }} data-id="element-308">
                <span className="text-sm font-semibold" data-id="element-309">Next</span>
                <ChevronRightIcon className="w-5 h-5" data-id="element-310" />
              </motion.button>
            </motion.div>
            <motion.div className="mt-4 sm:mt-5" variants={itemVariants} data-id="element-311">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto" data-id="element-312">
                <div className="w-full sm:w-auto" data-id="element-313">
                  <p className="text-center text-sm font-semibold text-white mb-2" data-id="element-314">
                    Copy this email and add it to your indeed account
                  </p>
                  <div className="flex items-center bg-white/10 border border-white/20 rounded-xl overflow-hidden" data-id="element-315">
                    <code className="px-4 py-3 text-white font-mono text-sm sm:text-base flex-1" data-id="element-316">
                      indeed@thehireadvantage.co
                    </code>
                    <motion.button onClick={() => {
                    const email = 'indeed@thehireadvantage.co';
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(email).then(() => {
                        alert('Email copied to clipboard!');
                      }).catch(() => {
                        const textArea = document.createElement('textarea');
                        textArea.value = email;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('Email copied to clipboard!');
                      });
                    } else {
                      const textArea = document.createElement('textarea');
                      textArea.value = email;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      alert('Email copied to clipboard!');
                    }
                  }} className="px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base bg-[#7fe0c3] hover:bg-[#6dd4b3] text-white font-bold transition-colors" whileHover={{
                    backgroundColor: '#6dd4b3'
                  }} whileTap={{
                    scale: 0.95
                  }} data-id="element-317">
                      Copy
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.main>
        </div>
      </HeroHighlight>
    </div>;
}